import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  background: #0a0a14;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #10101c;
  border: 1px solid #2a2a44;
  border-radius: 16px;
  padding: 40px 32px;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 520px) {
    padding: 32px 20px;
  }
`;

const HeaderBar = styled.div`
  height: 1px;
  width: 56px;
  background: linear-gradient(90deg, transparent, #E6C77E, transparent);
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(1.6rem, 3.5vw, 2rem);
  color: ${props => props.theme.colors.text};
  margin: 0 0 10px 0;
  line-height: 1.2;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.85;
  margin: 0 0 32px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 10px;
`;

const RequiredMark = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 16px;
  background: ${props => props.theme.colors.bgPrimary};
  border: 1px solid ${props => props.$hasError ? 'rgba(239, 68, 68, 0.5)' : props.theme.colors.border};
  border-radius: 10px;
  color: ${props => props.theme.colors.text};
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  font-family: ${props => props.theme.fonts.main};

  &::placeholder { color: ${props => props.theme.colors.textMuted}; }
  &:focus {
    border-color: ${props => props.$hasError ? '#EF4444' : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(239,68,68,0.15)' : props.theme.colors.primaryGlow};
  }
`;

const HelperText = styled.p`
  font-size: 0.78rem;
  color: ${props => props.$error ? '#f87171' : props.theme.colors.textMuted};
  margin: 8px 0 0 0;
  line-height: 1.6;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: ${props => props.theme.colors.gradGold};
  color: #1A1408;
  border: 1px solid rgba(230,199,126,0.55);
  border-radius: 9999px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${props => props.theme.shadows.glowSoft};

  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: ${props => props.theme.shadows.glow}; }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 0.84rem;
  color: #f87171;
  line-height: 1.5;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  gap: 16px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => props.theme.colors.lineFaint};
  }
`;

const DividerText = styled.span`
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.textMuted};
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 13px 24px;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.lineSoft};
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    color: ${props => props.theme.colors.starGold};
    border-color: ${props => props.theme.colors.lineGold};
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 24px;
  cursor: pointer;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;

  a {
    color: ${props => props.theme.colors.starGold};
    text-decoration: none;
    font-weight: 500;
    &:hover { opacity: 0.85; }
  }
`;

const Checkbox = styled.input`
  margin-top: 2px;
  accent-color: #E6C77E;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
`;

const FooterLink = styled.div`
  text-align: center;
  margin-top: 24px;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};

  a {
    color: ${props => props.theme.colors.starGold};
    text-decoration: none;
    font-weight: 500;
    transition: opacity 0.2s;

    &:hover { opacity: 0.85; }
  }
`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const RegisterPage = () => {
  const { register, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const passwordTooShort = password.length > 0 && password.length < 8;
  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('パスワードは8文字以上で入力してください', 'Password must be at least 8 characters'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('パスワードが一致しません', 'Passwords do not match'));
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      const msg = err.response?.data?.detail || t('登録に失敗しました', 'Registration failed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isValid = name.trim().length > 0 && email.trim().length > 0 && password.length >= 8 && password === confirmPassword && agreedToTerms;

  return (
    <PageWrapper>
      <Card>
        <HeaderBar />
        <Title>{t('新規登録', 'Create Account')}</Title>
        <Description>{t('メールアドレスとパスワードで登録', 'Sign up with your email and password')}</Description>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t('名前', 'Name')}<RequiredMark>*</RequiredMark></Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('例: 田中 太郎', 'e.g. John Smith')}
              disabled={loading}
              autoComplete="name"
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('メールアドレス', 'Email')}<RequiredMark>*</RequiredMark></Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              autoComplete="email"
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('パスワード', 'Password')}<RequiredMark>*</RequiredMark></Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('8文字以上', 'At least 8 characters')}
              disabled={loading}
              autoComplete="new-password"
              $hasError={passwordTooShort}
            />
            {passwordTooShort && (
              <HelperText $error>{t('パスワードは8文字以上で入力してください', 'Password must be at least 8 characters')}</HelperText>
            )}
          </FormGroup>

          <FormGroup>
            <Label>{t('パスワード確認', 'Confirm Password')}<RequiredMark>*</RequiredMark></Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('パスワードを再入力', 'Re-enter password')}
              disabled={loading}
              autoComplete="new-password"
              $hasError={passwordMismatch}
            />
            {passwordMismatch && (
              <HelperText $error>{t('パスワードが一致しません', 'Passwords do not match')}</HelperText>
            )}
          </FormGroup>

          <CheckboxRow>
            <Checkbox
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              disabled={loading}
            />
            <span>
              <Link to="/legal/terms" target="_blank">{t('利用規約', 'Terms of Service')}</Link>
              {t('および', ' and ')}
              <Link to="/legal/privacy" target="_blank">{t('プライバシーポリシー', 'Privacy Policy')}</Link>
              {t('に同意する', '')}
            </span>
          </CheckboxRow>

          <SubmitButton type="submit" disabled={loading || !isValid}>
            {loading ? <><Spinner />{t('登録中...', 'Creating account...')}</> : t('登録', 'Create Account')}
          </SubmitButton>
        </form>

        <Divider><DividerText>{t('または', 'or')}</DividerText></Divider>

        <GoogleButton onClick={login}>
          <GoogleIcon />
          {t('Googleで登録', 'Sign up with Google')}
        </GoogleButton>

        <FooterLink>
          {t('アカウントをお持ちの方は', 'Already have an account? ')}<Link to="/login">{t('ログイン', 'Login')}</Link>
        </FooterLink>
      </Card>
    </PageWrapper>
  );
};

export default RegisterPage;
